/**
 * PaymentHistoryTable
 * Displays collection records (payment history) for a specific credit.
 * Shows payment type, amount, status, and date for each record.
 */

import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import type { CollectionRecord } from '@/types/collection.types';
import {
  formatCurrencyMXN,
  COLLECTION_STATUS_LABEL,
  COLLECTION_STATUS_VARIANT,
  PAYMENT_TYPE_LABEL,
  PAYMENT_TYPE_VARIANT,
} from '@/features/collection/types';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

interface PaymentHistoryTableProps {
  records: CollectionRecord[];
  isLoading: boolean;
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function PaymentHistoryTable({ records, isLoading }: PaymentHistoryTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={`payment-skeleton-${i.toString()}`} variant="rectangular" height={40} />
        ))}
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Sin movimientos registrados para este credito
      </div>
    );
  }

  // Sort by date descending (most recent first)
  const sorted = [...records].sort(
    (a, b) => new Date(b.collection_date).getTime() - new Date(a.collection_date).getTime(),
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="px-4 py-2 font-medium">Fecha</th>
            <th className="px-4 py-2 font-medium">Cupon</th>
            <th className="px-4 py-2 font-medium">Tipo</th>
            <th className="px-4 py-2 text-right font-medium">Esperado</th>
            <th className="px-4 py-2 text-right font-medium">Cobrado</th>
            <th className="px-4 py-2 text-right font-medium">Principal</th>
            <th className="px-4 py-2 text-right font-medium">Interes</th>
            <th className="px-4 py-2 text-right font-medium">IVA</th>
            <th className="px-4 py-2 font-medium">Estado</th>
            <th className="px-4 py-2 font-medium">Referencia</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((record) => (
            <tr
              key={record.record_id}
              className="border-b border-border/50 transition-colors hover:bg-muted/30"
            >
              <td className="whitespace-nowrap px-4 py-2 text-foreground">
                {formatDateShort(record.collection_date)}
              </td>
              <td className="px-4 py-2 text-foreground">{record.coupon_number}</td>
              <td className="px-4 py-2">
                <Badge
                  variant={(PAYMENT_TYPE_VARIANT[record.payment_type] ?? 'default') as BadgeVariant}
                >
                  {PAYMENT_TYPE_LABEL[record.payment_type] ?? record.payment_type}
                </Badge>
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-foreground">
                {formatCurrencyMXN(record.amount_requested_mxn)}
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-foreground">
                {formatCurrencyMXN(record.amount_collected_mxn)}
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-foreground">
                {formatCurrencyMXN(record.principal_collected_mxn)}
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-foreground">
                {formatCurrencyMXN(record.interest_collected_mxn)}
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-foreground">
                {formatCurrencyMXN(record.iva_collected_mxn ?? 0)}
              </td>
              <td className="px-4 py-2">
                <Badge
                  variant={(COLLECTION_STATUS_VARIANT[record.collection_status] ?? 'default') as BadgeVariant}
                >
                  {COLLECTION_STATUS_LABEL[record.collection_status] ?? record.collection_status}
                </Badge>
              </td>
              <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                {record.reference || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
